import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { Post } from "@family/shared";

interface PostReactionsProps {
  familySlug: string;
  post: Post;
}

export function PostReactions({ familySlug, post }: PostReactionsProps) {
  const queryClient = useQueryClient();
  const heartCount = post.reactions?.heart ?? 0;
  const hasHearted = post.myReactions?.includes("heart") ?? false;

  const toggle = useMutation({
    mutationFn: () =>
      apiFetch(`/families/${familySlug}/posts/${post.id}/reactions`, {
        method: "POST",
        body: JSON.stringify({ kind: "heart" }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["post", post.id] });
    },
  });

  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      aria-label={hasHearted ? "Remove heart" : "Heart this post"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 12px",
        minHeight: 34,
        background: hasHearted ? "rgba(220, 38, 38, 0.08)" : "transparent",
        color: hasHearted ? "#dc2626" : "var(--c-text-subtle)",
        border: "1px solid",
        borderColor: hasHearted ? "rgba(220, 38, 38, 0.3)" : "var(--c-border)",
        borderRadius: "var(--r-pill, 999px)",
        cursor: "pointer",
        fontSize: "0.9rem",
        fontWeight: hasHearted ? 700 : 400,
        transition: "all 0.15s ease",
      }}
    >
      <span style={{ fontSize: "1.1rem" }}>{hasHearted ? "❤️" : "🤍"}</span>
      {heartCount > 0 && <span>{heartCount}</span>}
    </button>
  );
}
